<template>
  <div>
    <b-row class="content-header">
      <b-col
        class="content-header-left mb-2"
        cols="12"
        md="12"
      >
        <b-row class="breadcrumbs-top">
          <b-col cols="12">
            <h1 class="custom-header-title">
              {{ catalog.stringID }} - {{ catalog.name }}
            </h1>
            <p class="log_info">
              Created by {{ catalog.updatedBy ? catalog.updatedBy.name : '' }} on {{ dateFormatWithTime(catalog.createdAt) }}<br>
              Last updated on {{ dateFormatWithTime(catalog.updatedAt) }}
            </p>
          </b-col>
        </b-row>
      </b-col>
    </b-row>

    <b-row>
      <b-col md="12">
        <b-card header-tag="header">
          <div class="d-flex justify-content-between">
            <b-table-simple
              borderless
              responsive
              class="custom-th-width"
            >
              <b-tbody>
                <b-tr>
                  <b-th width="230">
                    Catalog Item Name
                  </b-th>
                  <b-td>{{ catalog.name || '-' }}</b-td>
                </b-tr>
                <b-tr>
                  <b-th width="230">
                    Product Category
                  </b-th>
                  <b-td>
                    <b-button
                      v-if="catalog.category"
                      variant="link"
                      class="p-0"
                      :to="{ name: 'products-categories-show', params: { id: catalog.category._id } }"
                    >
                      {{ catalog.category.name }}
                    </b-button>
                    <span v-else>-</span>
                  </b-td>
                </b-tr>
                <b-tr>
                  <b-th width="230">
                    Product Code
                  </b-th>
                  <b-td>
                    <span v-if="catalog.productCodes && catalog.productCodes.length">
                      <span
                        v-for="(pc, index) in catalog.productCodes"
                        :key="pc._id"
                      >
                        <b-button
                          variant="link"
                          class="p-0"
                          :to="{ name: 'products-codes-show', params: { id: pc._id } }"
                        >
                          {{ pc.name }}
                        </b-button>
                        <span v-if="index < catalog.productCodes.length - 1">, </span>
                      </span>
                    </span>
                    <span v-else>-</span>
                  </b-td>
                </b-tr>
                <b-tr>
                  <b-th width="230">
                    Image
                  </b-th>
                  <b-td>
                    <b-img
                      v-if="catalog.image"
                      :src="catalog.image"
                      height="185"
                      width="185"
                    />
                    <span v-else>-</span>
                  </b-td>
                </b-tr>
                <b-tr>
                  <b-th width="230">
                    SCLC Package ID
                  </b-th>
                  <b-td>{{ catalog.sclcID || '-' }}</b-td>
                </b-tr>
                <b-tr>
                  <b-th width="230">
                    SCLC Package Code
                  </b-th>
                  <b-td>{{ catalog.code || '-' }}</b-td>
                </b-tr>
                <b-tr>
                  <b-th width="230">
                    SCLC Description
                  </b-th>
                  <b-td>{{ catalog.sclcName || '-' }}</b-td>
                </b-tr>
                <b-tr>
                  <b-th width="230">
                    Notes for Agent
                  </b-th>
                  <b-td>
                    <!-- eslint-disable -->
                    <div
                      v-if="catalog.agentNote"
                      v-html="catalog.agentNote"
                    />
                    <!-- eslint-enable -->
                    <span v-else>-</span>
                  </b-td>
                </b-tr>
                <b-tr>
                  <b-th width="230">
                    Status
                  </b-th>
                  <b-td>
                    <span :class="catalog.status === 'On Sale' ? 'text-success' : 'text-secondary'">
                      {{ catalog.status || '-' }}
                    </span>
                  </b-td>
                </b-tr>
              </b-tbody>
            </b-table-simple>

            <b-button
              v-if="canViewThisAction('update', 'ProductCatalog')"
              variant="flat-primary"
              class="display-content"
              :to="{ name: 'products-catalog-edit', params: { id: $route.params.id } }"
            >
              <feather-icon
                icon="EditIcon"
                class="mr-50"
                size="16"
              />
              <span class="align-middle">Edit</span>
            </b-button>
          </div>
        </b-card>
      </b-col>
    </b-row>

    <!-- Pricing + Discounts Section -->
    <b-row v-if="canViewThisAction('read-price', 'ProductCatalog') || canViewThisAction('read-discount', 'ProductCatalog')">
      <b-col md="12">
        <b-card
          no-body
          class="shadow-none need-types-card"
        >
          <div
            v-if="loadingPricesDiscounts"
            class="text-center py-3"
          >
            <b-spinner small />
            <span class="ml-1">Loading...</span>
          </div>

          <!-- Single product code: flat tables -->
          <template v-else-if="!catalog.productCodes || catalog.productCodes.length <= 1">
            <div class="d-flex justify-content-between align-items-center pricing-table-header">
              <b-card-title class="mb-0">
                Pricing
              </b-card-title>
              <div class="d-flex align-items-center">
                <b-input-group
                  v-if="showSearchBar"
                  class="input-group-merge mr-50"
                >
                  <b-form-input
                    v-model="searchQuery"
                    placeholder="Search pricing..."
                  />
                  <b-input-group-append is-text>
                    <feather-icon
                      icon="XIcon"
                      class="cursor-pointer"
                      @click="searchQuery = ''; showSearchBar = false"
                    />
                  </b-input-group-append>
                </b-input-group>
                <a
                  href="javascript:void(0)"
                  class="nav-link nav-link-search p-0 flex-shrink-0"
                  style="white-space: nowrap;"
                  @click="showSearchBar = !showSearchBar"
                >
                  <feather-icon
                    icon="SearchIcon"
                    size="21"
                    class="mr-50"
                  />
                  <span class="align-middle">Search</span>
                </a>
              </div>
            </div>
            <b-table
              :items="filteredSinglePrices"
              :fields="priceFields"
              responsive
              show-empty
              empty-text="No pricing data available."
              class="mb-0 shadow-table"
            >
              <template #cell(needType)="data">
                {{ data.item.needType ? (data.item.needType.displayLabel || data.item.needType.needType) : '-' }}
              </template>
              <template #cell(availableToAgents)="data">
                {{ data.item.needType && data.item.needType.availableToAgents ? 'Yes' : 'No' }}
              </template>
              <template #cell(price)="data">
                {{ data.item.price != null ? `$${Number(data.item.price).toLocaleString()}` : '-' }}
              </template>
              <template #cell(effectiveDate)="data">
                {{ data.item.effectiveDate ? dateFormatWithTime(data.item.effectiveDate) : '-' }}
              </template>
            </b-table>

            <template v-if="canViewThisAction('read-discount', 'ProductCatalog')">
              <div class="d-flex justify-content-between align-items-center pricing-table-header mt-3">
                <b-card-title class="mb-0">
                  Discounts
                </b-card-title>
                <div class="d-flex align-items-center">
                  <b-input-group
                    v-if="showDiscountSearchBar"
                    class="input-group-merge mr-50"
                  >
                    <b-form-input
                      v-model="discountSearchQuery"
                      placeholder="Search discounts..."
                    />
                    <b-input-group-append is-text>
                      <feather-icon
                        icon="XIcon"
                        class="cursor-pointer"
                        @click="discountSearchQuery = ''; showDiscountSearchBar = false"
                      />
                    </b-input-group-append>
                  </b-input-group>
                  <a
                    href="javascript:void(0)"
                    class="nav-link nav-link-search p-0 flex-shrink-0"
                    style="white-space: nowrap;"
                    @click="showDiscountSearchBar = !showDiscountSearchBar"
                  >
                    <feather-icon
                      icon="SearchIcon"
                      size="21"
                      class="mr-50"
                    />
                    <span class="align-middle">Search</span>
                  </a>
                </div>
              </div>
              <b-table
                :items="filteredSingleDiscounts"
                :fields="discountFields"
                responsive
                show-empty
                empty-text="No discount data available."
                class="mb-0 shadow-table"
              >
                <template #cell(needType)="data">
                  {{ data.item.needType ? (data.item.needType.displayLabel || data.item.needType.needType) : '-' }}
                </template>
                <template #cell(price)="data">
                  {{ data.item.price != null ? `$${Number(data.item.price).toLocaleString()}` : '-' }}
                </template>
                <template #cell(effectiveDate)="data">
                  {{ data.item.effectiveDate ? dateFormatWithTime(data.item.effectiveDate) : '-' }}
                </template>
              </b-table>
            </template>
          </template>

          <!-- Multiple product codes: tabs -->
          <template v-else-if="catalog.productCodes && catalog.productCodes.length > 1">
            <b-tabs
              class="purchaser-tabs"
              content-class="mt-2"
            >
              <b-tab
                v-for="pc in catalog.productCodes"
                :key="pc._id"
                :title="`${pc.name} (Product Code: ${pc.code})`"
              >
                <b-card-text>
                  <div class="d-flex justify-content-between align-items-center pricing-table-header">
                    <b-card-title class="mb-0">
                      Pricing
                    </b-card-title>
                    <div class="d-flex align-items-center">
                      <b-input-group
                        v-if="showSearchBar"
                        class="input-group-merge mr-50"
                      >
                        <b-form-input
                          v-model="searchQuery"
                          placeholder="Search pricing..."
                        />
                        <b-input-group-append is-text>
                          <feather-icon
                            icon="XIcon"
                            class="cursor-pointer"
                            @click="searchQuery = ''; showSearchBar = false"
                          />
                        </b-input-group-append>
                      </b-input-group>
                      <a
                        href="javascript:void(0)"
                        class="nav-link nav-link-search p-0 flex-shrink-0"
                        style="white-space: nowrap;"
                        @click="showSearchBar = !showSearchBar"
                      >
                        <feather-icon
                          icon="SearchIcon"
                          size="21"
                          class="mr-50"
                        />
                        <span class="align-middle">Search</span>
                      </a>
                    </div>
                  </div>
                  <b-table
                    :items="filterPrices(pricesForGroup(pc._id))"
                    :fields="priceFields"
                    responsive
                    show-empty
                    empty-text="No pricing data available."
                    class="mb-0 shadow-table"
                  >
                    <template #cell(needType)="data">
                      {{ data.item.needType ? (data.item.needType.displayLabel || data.item.needType.needType) : '-' }}
                    </template>
                    <template #cell(availableToAgents)="data">
                      {{ data.item.needType && data.item.needType.availableToAgents ? 'Yes' : 'No' }}
                    </template>
                    <template #cell(price)="data">
                      {{ data.item.price != null ? `$${Number(data.item.price).toLocaleString()}` : '-' }}
                    </template>
                    <template #cell(effectiveDate)="data">
                      {{ data.item.effectiveDate ? dateFormatWithTime(data.item.effectiveDate) : '-' }}
                    </template>
                  </b-table>

                  <template v-if="canViewThisAction('read-discount', 'ProductCatalog')">
                    <div class="d-flex justify-content-between align-items-center pricing-table-header mt-3">
                      <b-card-title class="mb-0">
                        Discounts
                      </b-card-title>
                      <div class="d-flex align-items-center">
                        <b-input-group
                          v-if="showDiscountSearchBar"
                          class="input-group-merge mr-50"
                        >
                          <b-form-input
                            v-model="discountSearchQuery"
                            placeholder="Search discounts..."
                          />
                          <b-input-group-append is-text>
                            <feather-icon
                              icon="XIcon"
                              class="cursor-pointer"
                              @click="discountSearchQuery = ''; showDiscountSearchBar = false"
                            />
                          </b-input-group-append>
                        </b-input-group>
                        <a
                          href="javascript:void(0)"
                          class="nav-link nav-link-search p-0 flex-shrink-0"
                          style="white-space: nowrap;"
                          @click="showDiscountSearchBar = !showDiscountSearchBar"
                        >
                          <feather-icon
                            icon="SearchIcon"
                            size="21"
                            class="mr-50"
                          />
                          <span class="align-middle">Search</span>
                        </a>
                      </div>
                    </div>
                    <b-table
                      :items="filterDiscounts(discountsForGroup(pc._id))"
                      :fields="discountFields"
                      responsive
                      show-empty
                      empty-text="No discount data available."
                      class="mb-0 shadow-table"
                    >
                      <template #cell(needType)="data">
                        {{ data.item.needType ? (data.item.needType.displayLabel || data.item.needType.needType) : '-' }}
                      </template>
                      <template #cell(price)="data">
                        {{ data.item.price != null ? `$${Number(data.item.price).toLocaleString()}` : '-' }}
                      </template>
                      <template #cell(effectiveDate)="data">
                        {{ data.item.effectiveDate ? dateFormatWithTime(data.item.effectiveDate) : '-' }}
                      </template>
                    </b-table>
                  </template>
                </b-card-text>
              </b-tab>
            </b-tabs>
          </template>

          <div
            v-else
            class="text-secondary"
          >
            No pricing data available.
          </div>
        </b-card>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import {
  BCard, BRow, BCol, BButton, BImg, BTableSimple, BTbody, BTr, BTd, BTh,
  BTable, BTabs, BTab, BCardTitle, BCardText,
  BFormInput, BInputGroup, BInputGroupAppend, BSpinner,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { useUtils as useAclUtils } from '@/libs/acl/custom'
import store from '@/store/index'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BButton,
    BImg,
    BTableSimple,
    BTbody,
    BTh,
    BTr,
    BTd,
    BTable,
    BTabs,
    BTab,
    BCardTitle,
    BCardText,
    BFormInput,
    BInputGroup,
    BInputGroupAppend,
    BSpinner,
    // eslint-disable-next-line vue/no-unused-components
    ToastificationContent,
  },
  directives: {
    Ripple,
  },
  setup() {
    const { canViewThisAction } = useAclUtils()
    return { canViewThisAction }
  },
  data() {
    return {
      catalog: {},
      pricingGrouped: [],
      discountsGrouped: [],
      loadingPricesDiscounts: true,
      searchQuery: '',
      showSearchBar: false,
      discountSearchQuery: '',
      showDiscountSearchBar: false,
      priceFields: [
        { key: 'codeId', label: 'Code ID' },
        { key: 'needType', label: 'Need Type' },
        { key: 'availableToAgents', label: 'Available to Agents' },
        { key: 'description', label: 'Description' },
        { key: 'price', label: 'Amount' },
        { key: 'effectiveDate', label: 'Effective Date' },
      ],
      discountFields: [
        { key: 'codeId', label: 'Code ID' },
        { key: 'needType', label: 'Need Type' },
        { key: 'description', label: 'Description' },
        { key: 'price', label: 'Amount' },
        { key: 'effectiveDate', label: 'Effective Date' },
      ],
    }
  },
  computed: {
    filteredSinglePrices() {
      return this.filterPrices(this.pricingGrouped[0] ? this.pricingGrouped[0].prices : [])
    },
    filteredSingleDiscounts() {
      return this.filterDiscounts(this.discountsGrouped[0] ? this.discountsGrouped[0].discounts : [])
    },
  },
  created() {
    this.$http.get(`reservation/product-catalog/${this.$route.params.id}`)
      .then(response => {
        this.catalog = response.data

        store.commit('breadcrumb/REMOVE_BREADCRUMB')
        const newMeta = {
          active: true,
          text: this.catalog.name,
        }
        store.commit('breadcrumb/UPDATE_BREADCRUMB', newMeta)

        return Promise.all([
          this.$http.get(`reservation/product-catalog-price/by-catalog/${this.$route.params.id}`),
          this.$http.get(`reservation/product-catalog-discount/by-catalog/${this.$route.params.id}`),
        ])
      })
      .then(([priceResponse, discountResponse]) => {
        if (priceResponse) this.pricingGrouped = priceResponse.data.grouped || []
        if (discountResponse) this.discountsGrouped = discountResponse.data.grouped || []
        this.loadingPricesDiscounts = false
      })
      .catch(error => {
        this.loadingPricesDiscounts = false
        this.$toast({
          component: ToastificationContent,
          props: {
            title: error.data?.message ?? process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  },
  methods: {
    filterPrices(prices) {
      if (!this.searchQuery) return prices
      const q = this.searchQuery.toLowerCase()
      return prices.filter(p => {
        const needType = p.needType ? (p.needType.displayLabel || p.needType.needType || '') : ''
        const description = p.description || ''
        return needType.toLowerCase().includes(q) || description.toLowerCase().includes(q)
      })
    },
    filterDiscounts(discounts) {
      if (!this.discountSearchQuery) return discounts
      const q = this.discountSearchQuery.toLowerCase()
      return discounts.filter(d => {
        const needType = d.needType ? (d.needType.displayLabel || d.needType.needType || '') : ''
        const description = d.description || ''
        return needType.toLowerCase().includes(q) || description.toLowerCase().includes(q)
      })
    },
    pricesForGroup(pcId) {
      if (!pcId) return []
      const found = this.pricingGrouped.find(g => g.productCode && g.productCode._id === pcId)
      return found ? found.prices : []
    },
    discountsForGroup(pcId) {
      if (!pcId) return []
      const found = this.discountsGrouped.find(g => g.productCode && g.productCode._id === pcId)
      return found ? found.discounts : []
    },
  },
}
</script>

<style lang="scss">
.display-content {
  display: contents;
}
.custom-th-width {
  .table th {
    padding: 0.72rem 2rem 0.72rem 0.72rem;
  }
}
.content-body .card.need-types-card {
  padding: 40px !important;
}

.pricing-table-header {
  margin-bottom: 18px !important;
}
</style>
